import React from 'react';
import { InvoiceAssociationInfo } from '@/api/rebates/types';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  CircleNotchIcon,
  WarningIcon,
  InfoIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { formatPaymentDate } from '@/utils/dateUtils';
import { Separator } from '@/components/ui/separator';
import { T, Var } from 'gt-react';

interface DeleteRebateInvoiceModalProps {
  open: boolean;
  invoiceInfo: InvoiceAssociationInfo | null;
  loading: boolean;
  showAdvanced: boolean;
  forceDeleteConfirmText: string;
  onSetShowAdvanced: (show: boolean) => void;
  onSetForceDeleteConfirmText: (text: string) => void;
  onConfirmDelete: (force?: boolean) => void;
  onCancel: () => void;
}

export function DeleteRebateInvoiceModal({
  open,
  invoiceInfo,
  loading,
  showAdvanced,
  forceDeleteConfirmText,
  onSetShowAdvanced,
  onSetForceDeleteConfirmText,
  onConfirmDelete,
  onCancel,
}: DeleteRebateInvoiceModalProps) {
  if (!invoiceInfo) return null;

  const isSingleAssociation = invoiceInfo.associationCount === 1;
  const isForceDeleteReady =
    forceDeleteConfirmText === invoiceInfo.invoiceNumber;
  const currentAgreementAssoc = invoiceInfo.associations[0]; // Assuming we're always viewing from one agreement's perspective

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent
        className={`z-[1500] max-w-2xl max-h-[90vh] overflow-y-auto`}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <WarningIcon className="h-5 w-5 text-destructive" />
            {isSingleAssociation ? (
              <T context="delete rebate invoice modal title single association">
                Delete Vendor Reported Amount
              </T>
            ) : (
              <T context="delete rebate invoice modal title multiple associations">
                Remove Invoice Association
              </T>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isSingleAssociation ? (
              <T context="delete rebate invoice modal description cascade delete">
                This invoice will be permanently deleted from the system.
              </T>
            ) : (
              <T context="delete rebate invoice modal description choose action">
                Choose how to handle this invoice association.
              </T>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* Invoice Summary Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">
                    <T context="rebate invoice delete summary label">
                      Invoice Number:
                    </T>
                  </span>
                  <span className="text-sm font-mono">
                    {invoiceInfo.invoiceNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">
                    <T context="rebate invoice delete summary label">Amount:</T>
                  </span>
                  <span className="text-sm font-semibold">
                    $
                    {invoiceInfo.totalAmount.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    {invoiceInfo.currency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">
                    <T context="rebate invoice delete summary label">
                      Line Items:
                    </T>
                  </span>
                  <span className="text-sm">{invoiceInfo.lineItemCount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">
                    <T context="rebate invoice delete summary label">
                      Documents:
                    </T>
                  </span>
                  <span className="text-sm">
                    {invoiceInfo.documentCount === 1 ? (
                      <T context="rebate invoice document count in summary">
                        1 file
                      </T>
                    ) : (
                      <T context="rebate invoice document count in summary">
                        <Var>{invoiceInfo.documentCount}</Var> files
                      </T>
                    )}
                  </span>
                </div>
                {invoiceInfo.lineItemDateRange && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">
                      <T context="rebate invoice delete summary label">
                        Date Range:
                      </T>
                    </span>
                    <span className="text-sm">
                      {formatPaymentDate(
                        invoiceInfo.lineItemDateRange.earliest,
                      )}{' '}
                      -{' '}
                      {formatPaymentDate(invoiceInfo.lineItemDateRange.latest)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {isSingleAssociation ? (
            /* Single Association - Cascade Delete Warning */
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                <WarningIcon className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">
                    <T context="rebate invoice cascade delete warning">
                      This invoice is ONLY used by this agreement
                    </T>
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <p className="font-medium">
                  <T context="rebate invoice permanent delete list heading">
                    The following will be PERMANENTLY DELETED:
                  </T>
                </p>
                <ul className="space-y-1 ml-4">
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    <T context="rebate invoice delete cascade item">
                      Invoice record
                    </T>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    <T context="rebate invoice delete cascade item line count">
                      All <Var>{invoiceInfo.lineItemCount}</Var> line items
                    </T>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    {invoiceInfo.documentCount === 1 ? (
                      <T context="rebate invoice delete cascade item documents">
                        1 document file (original + transformed)
                      </T>
                    ) : (
                      <T context="rebate invoice delete cascade item documents">
                        <Var>{invoiceInfo.documentCount}</Var> document files
                        (original + transformed)
                      </T>
                    )}
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    <T context="rebate invoice delete cascade item">
                      Forecast data for this agreement
                    </T>
                  </li>
                </ul>
              </div>

              <div className="flex items-start gap-2 p-3 bg-muted rounded-md">
                <InfoIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  <T context="destructive action irreversible notice">
                    This action cannot be undone.
                  </T>
                </p>
              </div>
            </div>
          ) : (
            /* Multiple Associations - Remove or Force Delete Options */
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <InfoIcon className="h-4 w-4" />
                  {invoiceInfo.associationCount === 1 ? (
                    <T context="rebate invoice shared across agreements count">
                      Currently used by 1 agreement:
                    </T>
                  ) : (
                    <T context="rebate invoice shared across agreements count">
                      Currently used by{' '}
                      <Var>{invoiceInfo.associationCount}</Var> agreements:
                    </T>
                  )}
                </p>
                <div className="ml-6 space-y-1.5">
                  {invoiceInfo.associations.map((assoc) => (
                    <div
                      key={assoc.agreementId}
                      className="text-sm flex items-center gap-2"
                    >
                      <CheckCircleIcon className="h-3 w-3 text-green-600" />
                      {assoc.agreementName}
                      {assoc.agreementId ===
                        currentAgreementAssoc.agreementId && (
                        <span className="text-xs text-muted-foreground">
                          <T context="rebate invoice association list current marker">
                            (this one)
                          </T>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <p className="text-sm font-medium">
                  <T context="rebate invoice remove association section heading">
                    What will happen:
                  </T>
                </p>
                <ul className="space-y-1 ml-4 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <T context="rebate invoice remove association outcome">
                      Invoice remains in database
                    </T>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    <T context="rebate invoice remove association outcome">
                      Line items remain intact
                    </T>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    {invoiceInfo.associationCount - 1 === 1 ? (
                      <T context="rebate invoice remove association outcome">
                        Other 1 agreement keeps using this data
                      </T>
                    ) : (
                      <T context="rebate invoice remove association outcome">
                        Other <Var>{invoiceInfo.associationCount - 1}</Var>{' '}
                        agreements keep using this data
                      </T>
                    )}
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    <T context="rebate invoice remove association outcome negative">
                      Current agreement will no longer see this invoice
                    </T>
                  </li>
                  <li className="flex items-center gap-2">
                    <XCircleIcon className="h-4 w-4 text-destructive" />
                    <T context="rebate invoice remove association outcome negative">
                      Forecast for current agreement will be invalidated
                    </T>
                  </li>
                </ul>
              </div>

              {/* Advanced Options - Force Delete */}
              <div className="border border-destructive/20 rounded-md">
                <button
                  type="button"
                  className="w-full p-3 flex items-center justify-between hover:bg-accent/50 transition-colors rounded-t-md"
                  onClick={() => onSetShowAdvanced(!showAdvanced)}
                >
                  <span className="text-sm font-medium flex items-center gap-2">
                    <WarningIcon className="h-4 w-4 text-destructive" />
                    <T context="rebate invoice advanced force delete option">
                      Advanced: Delete from ALL agreements
                    </T>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {showAdvanced ? (
                      <T context="toggle advanced rebate delete section">
                        ▲ Hide
                      </T>
                    ) : (
                      <T context="toggle advanced rebate delete section">
                        ▼ Show
                      </T>
                    )}
                  </span>
                </button>

                {showAdvanced && (
                  <div className="p-4 space-y-3 border-t bg-destructive/5">
                    <p className="text-sm">
                      <T context="rebate invoice force delete all agreements intro">
                        This will PERMANENTLY delete the invoice from all{' '}
                        <Var>{invoiceInfo.associationCount}</Var> agreements:
                      </T>
                    </p>
                    <ul className="text-sm space-y-1 ml-4">
                      {invoiceInfo.associations.map((assoc) => (
                        <li key={assoc.agreementId}>• {assoc.agreementName}</li>
                      ))}
                    </ul>

                    <div className="space-y-2 pt-2">
                      <p className="text-sm font-medium">
                        <T context="rebate invoice force delete list heading">
                          ALL of the following will be deleted:
                        </T>
                      </p>
                      <ul className="space-y-1 ml-4 text-sm">
                        <li className="flex items-center gap-2">
                          <XCircleIcon className="h-4 w-4 text-destructive" />
                          <T context="rebate invoice force delete list item">
                            Invoice record
                          </T>
                        </li>
                        <li className="flex items-center gap-2">
                          <XCircleIcon className="h-4 w-4 text-destructive" />
                          <T context="rebate invoice force delete list item line count">
                            All <Var>{invoiceInfo.lineItemCount}</Var> line
                            items
                          </T>
                        </li>
                        <li className="flex items-center gap-2">
                          <XCircleIcon className="h-4 w-4 text-destructive" />
                          {invoiceInfo.documentCount === 1 ? (
                            <T context="rebate invoice force delete list item documents">
                              1 document file
                            </T>
                          ) : (
                            <T context="rebate invoice force delete list item documents">
                              <Var>{invoiceInfo.documentCount}</Var> document
                              files
                            </T>
                          )}
                        </li>
                        <li className="flex items-center gap-2">
                          <XCircleIcon className="h-4 w-4 text-destructive" />
                          <T context="rebate invoice force delete list item forecasts">
                            Forecasts for ALL{' '}
                            <Var>{invoiceInfo.associationCount}</Var> agreements
                          </T>
                        </li>
                      </ul>
                    </div>

                    <div className="space-y-2 pt-2">
                      <label
                        htmlFor="confirm-text"
                        className="text-sm font-medium"
                      >
                        <T context="rebate invoice force delete type to confirm label">
                          Type &quot;
                          <Var>{invoiceInfo.invoiceNumber}</Var>
                          &quot; to confirm:
                        </T>
                      </label>
                      <Input
                        id="confirm-text"
                        value={forceDeleteConfirmText}
                        onChange={(e) =>
                          onSetForceDeleteConfirmText(e.target.value)
                        }
                        placeholder={invoiceInfo.invoiceNumber ?? undefined}
                        className="font-mono"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading} onClick={onCancel}>
            <T context="dialog cancel button">Cancel</T>
          </AlertDialogCancel>

          {isSingleAssociation ? (
            <AlertDialogAction
              onClick={() => onConfirmDelete(false)}
              disabled={loading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {loading && (
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <T context="rebate invoice delete confirm destructive">
                Delete Permanently
              </T>
            </AlertDialogAction>
          ) : (
            <>
              <Button
                variant="destructive"
                onClick={() => onConfirmDelete(false)}
                disabled={loading}
              >
                {loading && (
                  <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                <T context="rebate invoice remove association button">
                  Remove from This Agreement
                </T>
              </Button>

              {showAdvanced && (
                <Button
                  variant="destructive"
                  onClick={() => onConfirmDelete(true)}
                  disabled={loading || !isForceDeleteReady}
                  className="bg-red-700 hover:bg-red-800"
                >
                  {loading && (
                    <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <T context="rebate invoice force delete all agreements button">
                    Delete from ALL Agreements
                  </T>
                </Button>
              )}
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
